// TEMPORARY test-only cancel control. Surfaces a "Cancel this booking"
// button on the congrats screen so the prod fan-out test can self-clean
// without paging CC ops. Gated by `window.CC_WIDGET_CONFIG.testCancelButton`.
//
// Comes out after launch (the snippet on the test page sets the flag; the
// customer-facing embed does not).

import { useState } from 'react';
import styles from '../styles/widget.module.css';
import { cancelBooking, ApiError } from '../api/client';
import type { BookingState } from '../state/machine';

type CancelState =
  | { kind: 'idle' }
  | { kind: 'confirming' }
  | { kind: 'submitting' }
  | { kind: 'success' }
  | { kind: 'error'; message: string };

function isEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return window.CC_WIDGET_CONFIG?.testCancelButton === true;
}

export function TestCancelButton({ state }: { state: BookingState }) {
  const [status, setStatus] = useState<CancelState>({ kind: 'idle' });

  if (!isEnabled()) return null;
  if (!state.confirmation) return null;

  const bookingId = state.confirmation.id;
  const email = state.confirmation.customer.email || state.email;

  const handleCancel = async () => {
    setStatus({ kind: 'submitting' });
    try {
      await cancelBooking(bookingId, { email: email.trim() || undefined });
      setStatus({ kind: 'success' });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `Cancel failed (${err.status}). ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Cancel failed.';
      setStatus({ kind: 'error', message: msg });
    }
  };

  return (
    <div className={styles.testCancelBlock}>
      <div className={styles.testCancelHeader}>
        <strong>Test-only — Cancel this booking</strong>
        <span className={styles.testCancelHint}>
          Temporary control on the prod test embed. Removed after launch.
        </span>
      </div>

      {status.kind === 'idle' && (
        <button
          type="button"
          className={styles.testCancelButton}
          onClick={() => setStatus({ kind: 'confirming' })}
        >
          Cancel this booking
        </button>
      )}

      {status.kind === 'confirming' && (
        <div className={styles.testCancelConfirm}>
          <p>
            Cancel <strong>{bookingId}</strong> against prod SF using identity{' '}
            <code>{email}</code>?
          </p>
          <div className={styles.testCancelActions}>
            <button
              type="button"
              className={styles.testCancelConfirmYes}
              onClick={handleCancel}
            >
              Yes, cancel
            </button>
            <button
              type="button"
              className={styles.testCancelConfirmNo}
              onClick={() => setStatus({ kind: 'idle' })}
            >
              Keep it
            </button>
          </div>
        </div>
      )}

      {status.kind === 'submitting' && (
        <div className={styles.testCancelState}>
          <span className={styles.spinner} />
          Canceling {bookingId}…
        </div>
      )}

      {status.kind === 'success' && (
        <div className={styles.testCancelSuccess}>
          ✓ Canceled {bookingId}. The SF record is in cancelled state. No
          further action needed.
        </div>
      )}

      {status.kind === 'error' && (
        <div className={styles.testCancelError}>
          <p>✗ {status.message}</p>
          <button
            type="button"
            className={styles.testCancelRetry}
            onClick={() => setStatus({ kind: 'idle' })}
          >
            Try again
          </button>
        </div>
      )}
    </div>
  );
}
